//#region Imports

import { Component } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Crud } from '../../shared/services/crud/crud';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { InputComponent } from '../../shared/components/input/input.component';
import { NgIf, NgOptimizedImage } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { Disabled } from '../../shared/directives/disabled/disabled.directive';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthService } from '../../shared/services/auth/auth.service';
import { ToastService } from '../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../shared/utils/functions';

//#endregion

interface UserForm {
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
}

@Component({
    selector: 'l-reset-pwd',
    templateUrl: './reset-pwd.component.html',
    imports: [
        ButtonComponent,
        FormsModule,
        InputComponent,
        NgIf,
        ReactiveFormsModule,
        TooltipModule,
        IconComponent,
        NgOptimizedImage,
        Disabled,
        RouterLink,
    ]
})
export class ResetPwdComponent extends Crud<UserForm> {

  constructor(
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly route: ActivatedRoute,
  ) {
    super();
  }

  public isLoading = false;

  public override form = new FormGroup<UserForm>(<UserForm>{
    password: new FormControl('', [Validators.required]),
    confirmPassword: new FormControl('', [Validators.required]),
  });

  public override mapField = {
    password: 'senha',
    confirmPassword: 'confirmar senha'
  };

  public async submit(): Promise<void> {
    try {
      this.isLoading = true;
      const { password, confirmPassword } = this.form.getRawValue();
      const resetPasswordCode = this.getResetPasswordCode();

      if (!resetPasswordCode) {
        this.toastService.error('Código de redefinição inválido ou ausente.');
        return;
      }

      const passwordError = this.validatePassword(password ?? '', confirmPassword ?? '');

      if (passwordError) {
        this.toastService.error(passwordError);
        return;
      }

      await this.authService.resetPassword(password ?? '', confirmPassword ?? '', resetPasswordCode);
      this.toastService.success('Senha redefinida com sucesso.');
    } catch (error: any) {
      this.toastService.error(getCrudErrorMessage(error, 'Não foi possível redefinir a senha.'));
    } finally {
      this.isLoading = false;
    }
  }

  private validatePassword(password: string, confirmPassword: string): string | null {
    if (!password.trim() || !confirmPassword.trim()) {
      return 'Preencha a nova senha e a confirmação.';
    }

    if (password.length < 6) {
      return 'É necessário enviar uma senha com 6 ou mais caracteres.';
    }

    if (password !== confirmPassword) {
      return 'As senhas não coincidem.';
    }

    return null;
  }

  private getResetPasswordCode(): string {
    const params = this.route.snapshot.queryParamMap;
    return (params.get('code') ?? params.get('token') ?? params.get('resetPasswordCode') ?? '').trim();
  }

}
